/**
 * The rows the progress panel shows, which are FEWER than the turns a run runs.
 *
 * A seven-turn run is three questions' worth of work: one framing step and, per
 * key question, the question itself plus its chained follow-ups. Listing all
 * seven put a reader in front of a flat list of near-identical rows whose labels
 * were the only thing distinguishing them. This module folds each chain into the
 * question it belongs to, so the panel lists what the user actually asked for.
 *
 * Folding is not hiding, and that distinction is the whole reason this is a
 * module with its own tests rather than a filter at the render site:
 *
 *   - The STATE is the branch's, not the key question turn's. A question whose
 *     own turn completed while its second follow-up is still running is NOT
 *     answered, and a row that said `done` there would be plainly wrong. That is
 *     the failure mode collapsing rows introduces, and `runRowState` is the
 *     answer to it — including a `partial` state, because a branch that answered
 *     the question and lost one follow-up is neither done nor failed.
 *   - The COUNTS are summed across the branch, so retrieval that happened inside
 *     the chain is still reported rather than disappearing with its row.
 *   - Every step is CARRIED on the row, so the panel can show the breakdown the
 *     summed counts are a sum of. A total that does not reconcile with what is on
 *     screen beside it is worse than no total.
 *
 * A turn that cannot be folded — no key question to fold into — keeps its own
 * row. Nothing is ever silently dropped: the panel's own counter comes from the
 * run's planned total, so a vanished step would show up as a count that does not
 * add up.
 */

import type { AdvisoryRunTurn, AdvisoryRunTurnSource } from './advisoryRunView';

/** The phase agent-svc gives the framing turn. */
export const ROOT_PHASE = '1-root';

/**
 * What a row stands for. `followUp` is the unfoldable case only — a chain step
 * whose key question is not in the list — never a normal row.
 */
export type RunRowKind = 'root' | 'question' | 'followUp';

/**
 * Six states, two more than a single turn has.
 *
 * `partial` exists because a branch can end both ways at once: the question was
 * answered and one of its follow-ups died. The run-level heading already makes
 * that distinction for the whole run (`runPartialTitle`) for the same reason —
 * work that survived must not be reported as a failure.
 * `skipped` is derived from the plan when an upstream failure means a step never
 * receives a turn record at all.
 */
export type RunRowState = 'pending' | 'running' | 'done' | 'partial' | 'failed' | 'skipped';

/**
 * One step behind a row: a question, its state, and what it retrieved.
 *
 * The key question's OWN turn is one of these, and listing it is what makes the
 * row's total reconcile. A row summing 15 + 24 + 30 = 69 while showing only the
 * 24 and the 30 is a total a reader cannot check, and they will try — the first
 * thing anyone does with a number beside a breakdown is add the breakdown up.
 */
export interface AdvisoryRunStep {
  label: string;
  question: string;
  /** `null` for the key question's own step; the chain position otherwise. */
  subquestionN: number | null;
  state: RunRowState;
  edges: number;
}

export interface AdvisoryRunRow {
  /** The leading turn's label. React key, and the title's last-resort fallback. */
  label: string;
  kind: RunRowKind;
  /** The leading turn's question text, or `''` when no source named it. */
  question: string;
  phase: string;
  keyQuestionPosition: number | null;
  /** Set only on the unfoldable chain-step row; `null` on every normal row. */
  subquestionN: number | null;
  state: RunRowState;
  /**
   * Turns behind this row, and how many of them completed.
   *
   * The row's share of the run's own "7 of 7 steps": folding the chain away left
   * that headline counting something no row displayed, and three rows reading
   * `1/1`, `3/3`, `3/3` are what add back up to it.
   *
   * `stepsDone` counts COMPLETED turns, not terminal ones — a branch that lost a
   * follow-up reads `2/3` beside "partly answered" rather than claiming 3.
   */
  stepsTotal: number;
  stepsDone: number;
  /** Retrieved passages across every turn this row stands for. */
  edges: number;
  /** Injected slots that arrived empty, across the same turns. */
  blankSlots: number;
  /**
   * Summed wall time across the turns that measured one, or `null` when none
   * did. Summed rather than spanned: chain steps run serially, so the total is
   * both what the branch cost and roughly how long it took.
   */
  wallSeconds: number | null;
  /**
   * Every step behind this row, the question's own first and then its chain — the
   * breakdown its summed counts are a sum OF.
   *
   * Empty for a row that is a single step: one card repeating the row it hangs
   * under explains nothing, and there is no total to reconcile.
   */
  steps: AdvisoryRunStep[];
  /**
   * Earlier steps this row retrieved from, minus two kinds that carry no
   * information: its own turns — a branch that read its own chain is reading
   * itself, which is what chaining is for — and the framing step, which is an
   * ancestor of EVERY turn by construction, so naming it under every question
   * says the same thing about all of them and distinguishes none.
   *
   * What survives is the interesting case: one key question that retrieved from
   * another.
   */
  sources: AdvisoryRunTurnSource[];
}

const isFailed = (turn: AdvisoryRunTurn): boolean =>
  turn.failed ||
  turn.status === 'failed' ||
  turn.status === 'rejected' ||
  turn.status === 'timeout';
const isSkipped = (turn: AdvisoryRunTurn): boolean => turn.status === 'skipped';
const isCompleted = (turn: AdvisoryRunTurn): boolean => turn.status === 'completed';
const isTerminal = (turn: AdvisoryRunTurn): boolean =>
  isFailed(turn) || isSkipped(turn) || isCompleted(turn);

/**
 * One state for a whole branch.
 *
 * Least-advanced wins while anything is still moving: a question is not answered
 * until its chain is, and reporting the key question turn's own `completed` while
 * two follow-ups are still queued is the one wrong answer this function exists to
 * rule out.
 */
export function runRowState(steps: readonly AdvisoryRunTurn[]): RunRowState {
  if (!steps.every(isTerminal)) {
    // `planned` is the status of a turn agent-svc has not posted yet. A branch
    // where nothing has been posted is queued; one where anything has started or
    // finished is running.
    const started = steps.some(
      step => isTerminal(step) || step.status === 'running' || step.status === 'pending'
    );
    return started ? 'running' : 'pending';
  }
  const completed = steps.filter(isCompleted).length;
  if (completed === steps.length) {
    return 'done';
  }
  if (completed > 0) {
    return 'partial';
  }
  return steps.every(isSkipped) ? 'skipped' : 'failed';
}

/**
 * Turns, folded into rows and put in reading order.
 *
 * Reading order is not execution order: a run posts every key question in
 * parallel and then every chain, so its own order is by phase. Grouping by key
 * question and sorting by the question's place in the plan is what makes the list
 * read like the question set the user confirmed. Rows the plan could not place
 * sort last and keep their incoming order rather than being interleaved on a
 * guessed position.
 */
export function runRows(turns: readonly AdvisoryRunTurn[]): AdvisoryRunRow[] {
  const groups = new Map<string, AdvisoryRunTurn[]>();
  const groupOrder: string[] = [];

  for (const turn of turns) {
    // The framing step is its own row, and so is any step with no key question to
    // fold into — a chain step whose branch is absent must still be visible.
    const key =
      turn.phase === ROOT_PHASE || !turn.keyQuestionId
        ? `label:${turn.label}`
        : `kq:${turn.keyQuestionId}`;
    const existing = groups.get(key);
    if (existing) {
      existing.push(turn);
    } else {
      groups.set(key, [turn]);
      groupOrder.push(key);
    }
  }

  return groupOrder
    .map((key, index) => {
      const steps = groups.get(key) ?? [];
      // The key question turn leads its branch. Falling back to the first step
      // covers the branch whose own question turn is missing: the row is then
      // named by a chain step rather than by nothing.
      const lead = steps.find(step => step.subquestionN === null) ?? steps[0];
      const ownLabels = new Set(steps.map(step => step.label));

      const sources: AdvisoryRunTurnSource[] = [];
      const seen = new Set<string>();
      for (const step of steps) {
        for (const source of step.sources) {
          if (
            ownLabels.has(source.label) ||
            seen.has(source.label) ||
            source.phase === ROOT_PHASE
          ) {
            continue;
          }
          seen.add(source.label);
          sources.push(source);
        }
      }

      const measured = steps
        .map(step => step.wallSeconds)
        .filter((seconds): seconds is number => seconds !== null);

      const row: AdvisoryRunRow = {
        label: lead.label,
        kind:
          lead.phase === ROOT_PHASE ? 'root' : lead.subquestionN === null ? 'question' : 'followUp',
        question: lead.question,
        phase: lead.phase,
        keyQuestionPosition: lead.keyQuestionPosition,
        subquestionN: lead.subquestionN,
        state: runRowState(steps),
        stepsTotal: steps.length,
        stepsDone: steps.filter(isCompleted).length,
        edges: steps.reduce((total, step) => total + step.edges, 0),
        blankSlots: steps.reduce((total, step) => total + step.blankSlots, 0),
        wallSeconds: measured.length > 0 ? measured.reduce((total, s) => total + s, 0) : null,
        steps:
          steps.length > 1
            ? [...steps]
                .sort((a, b) => (a.subquestionN ?? 0) - (b.subquestionN ?? 0))
                .map(step => ({
                  label: step.label,
                  question: step.question,
                  subquestionN: step.subquestionN,
                  // Each step keeps its OWN state. The row's state is the branch's;
                  // a reader opening it is asking about the steps individually.
                  state: runRowState([step]),
                  edges: step.edges,
                }))
            : [],
        sources,
      };
      return { row, index };
    })
    .sort((a, b) => {
      const bucket = (r: AdvisoryRunRow): number => (r.kind === 'root' ? 0 : 1);
      const position = (r: AdvisoryRunRow): number =>
        r.keyQuestionPosition ?? Number.MAX_SAFE_INTEGER;
      return (
        bucket(a.row) - bucket(b.row) ||
        position(a.row) - position(b.row) ||
        (a.row.subquestionN ?? 0) - (b.row.subquestionN ?? 0) ||
        a.index - b.index
      );
    })
    .map(({ row }) => row);
}
